import Login from 'app/pages/auth/Login';
import Error404Page from 'app/pages/misc/Error404Page';
import RequireAuth from 'app/shared-components/auth/RequireAuth';
import FeatureFlagProvider from 'app/shared-components/feature-flags/FeatureFlagProvider';
import Layout from 'app/shared-components/layout/Layout';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import LoginSSO from './pages/auth/LoginSSO';
import Logout from './pages/auth/Logout';
import SessionExpiredPage from 'app/pages/auth/SessionExpiredPage';
import OnboardReviewNew from './pages/first-run/stepper/OnboardReviewNew';
import PurchaseCardsWrapper from './pages/PurchaseCardsWrapper';
import ReviewOnboard from './pages/review/ReviewOnboard';
import {
  RequireAdminOrSecondary,
  RequireSupport,
  RoleRouteGuard,
} from './shared-components/auth/RequireUserRole';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import { DEFAULT_LANDING_PAGE } from './store/authSlice';
import EnvironmentStripe from './shared-components/layout/environment-stripe/EnvironmentStripe';

const ForgotPasswordPage = lazy(() => import('app/pages/auth/ForgotPasswordPage'));
const Register = lazy(() => import('app/pages/auth/register/Register'));
const VerifyUser = lazy(() => import('app/pages/auth/VerifyUser'));
const Landing = lazy(() => import('app/pages/auth/Landing'));
const PasskeyPrompt = lazy(() => import('app/pages/auth/PasskeyPrompt'));
const Overview = lazy(() => import('./pages/overview/Overview'));
const Style = lazy(() => import('./pages/style/Style'));
const OnBoard = lazy(() => import('./pages/onboard/OnBoard'));
const Team = lazy(() => import('./pages/team/Team'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const CardsPage = lazy(() => import('./pages/cards/CardsPage'));
const TransactionDeepLink = lazy(() => import('./pages/deep-link/TransactionDeepLink'));
const ControlCenter = lazy(() => import('./pages/control-center/ControlCenter'));
//Obsolete Route
//const Wallet = lazy(() => import('./pages/wallet/Wallet'));
//const Payables = lazy(() => import('./pages/payables/Payables'));

function RoutingTable(): JSX.Element {
  return (
    <FeatureFlagProvider>
      <EnvironmentStripe>
        <RoutingTableRoutes />
      </EnvironmentStripe>
    </FeatureFlagProvider>
  );
}

function RoutingTableRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={DEFAULT_LANDING_PAGE} />} />

      <Route
        path="/"
        element={
          <RequireAuth requireUser={false} requireOnboarded={false}>
            <Suspense fallback={<LoadingOverlay isLoading={true} blockInteraction={true} />}>
              <Outlet />
            </Suspense>
          </RequireAuth>
        }
      >
        <Route path="/login" element={<Login />} />
        <Route path="/login/sso" element={<LoginSSO />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/forgot-password/:username" element={<ForgotPasswordPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/:email" element={<Register />} />
        <Route path="/verify-user" element={<VerifyUser />} />
        <Route path="/verify-user/:username" element={<VerifyUser />} />

        <Route
          path="/onboard/*"
          element={
            <Suspense fallback={<LoadingOverlay isLoading={true} blockInteraction={true} />}>
              <OnBoard />
            </Suspense>
          }
        />
      </Route>

      {/* SEE-219: post-inactivity-logout message screen. Deliberately outside
          every RequireAuth group: the guard navigates here while the sign-out
          is still settling, and RequireAuth's "authenticated users leave
          public pages" redirect would bounce it during that window. */}
      <Route path="/logged-out" element={<SessionExpiredPage />} />

      {/* SEE-213: post-login passkey enrollment prompt. Login redirects
          eligible users here right after sign-in (Landing covers reloads).
          Rendered OUTSIDE Layout — a full-screen auth-style page (Figma 4230),
          not a dashboard view with app chrome. requireOnboarded is off: the
          prompt only needs an authenticated user, so it must not wait behind
          the companies + feature-flag spinner; that data loads in the
          background while the user reads the prompt. */}
      <Route
        path="/setup-passkey"
        element={
          <RequireAuth requireOnboarded={false} requireFeatureFlags={false}>
            <PasskeyPrompt />
          </RequireAuth>
        }
      />

      {/* Authenticated but not-yet-onboarded: review/onboard bypasses the onboarding check */}
      <Route
        path="/"
        element={
          <RequireAuth requireOnboarded={false}>
            <PurchaseCardsWrapper>
              <Layout />
            </PurchaseCardsWrapper>
          </RequireAuth>
        }
      >
        <Route
          path="/review/*"
          element={
            <RequireAdminOrSecondary>
              <ReviewOnboard />
            </RequireAdminOrSecondary>
          }
        />
        <Route
          path="/review/onboard"
          element={
            <RequireAdminOrSecondary>
              <OnboardReviewNew />
            </RequireAdminOrSecondary>
          }
        />
      </Route>

      <Route
        path="/"
        element={
          <RequireAuth>
            <PurchaseCardsWrapper>
              <Layout />
            </PurchaseCardsWrapper>
          </RequireAuth>
        }
      >
        {/* SEE-946: route guards consult roleAccess + useUserRole capability
            composites. Blocked users are redirected to their role's default
            landing instead of seeing a blank page or being silently dropped
            on an inappropriate dashboard. /cards used to fall back to
            rendering <Overview /> for non-admins, which is wrong for
            Secondary (no overview at all) and Approval (different landing). */}
        <Route
          path="/cards/*"
          element={
            <RoleRouteGuard canAccess={(role) => role.canManageUsersAndCards}>
              <CardsPage />
            </RoleRouteGuard>
          }
        />
        <Route
          path="/settings/*"
          element={
            <RoleRouteGuard canAccess={(role) => role.canAccessSettings}>
              <Settings />
            </RoleRouteGuard>
          }
        />
        <Route path="/transactions/:id" element={<TransactionDeepLink />} />
        <Route path="/landing" element={<Landing />} />
        {/* SEE-946: Secondary admins have no Overview surface — redirect them
            to their default landing before we even lazy-load the Overview
            module. The inner Overview routes also redirect, but blocking at
            the top here avoids dispatching transaction queries / Welcome
            page renders for a user who'll never see them. */}
        <Route
          path="/overview/*"
          element={
            <RoleRouteGuard canAccess={(role) => role.showsHomeIcon}>
              <Overview />
            </RoleRouteGuard>
          }
        />
        <Route path="/team/*" element={<Team />} />
        {/* SEE-265: Speedchain support staff only (User.is_support). */}
        <Route
          path="/control-center/*"
          element={
            <RequireSupport>
              <ControlCenter />
            </RequireSupport>
          }
        />
        <Route path="/profile/*" element={<ProfilePage />} />
        <Route path="/style" element={<Style />} />
        <Route path="/logout" element={<Logout />} />
      </Route>
      <Route path="*" element={<Error404Page />} />
    </Routes>
  );
}

export default RoutingTable;
